import { useEffect, useMemo, useState, useRef } from 'react';
import { useCubeApi } from '../use_cube_api/use_cube_api.hook';
export const useServerSideCubeQuery = (query, options = { pagination: { pageIndex: 0, pageSize: 5 } }) => {
    const { cubeApi: contextCubeApi, freshCubeApi } = useCubeApi({ disablePreAggs: options.disablePreAggs });
    const cubeApi = options.cubeApi ?? contextCubeApi;
    // During a manual refresh, load through the always-fresh client. It is read via a ref so it stays
    // out of the effect deps — that way reverting `forceFresh` after refresh can't trigger a redundant
    // pre-agg fetch that would overwrite the freshly loaded data.
    const activeApi = options.cubeApi ?? (options.forceFresh && freshCubeApi ? freshCubeApi : contextCubeApi);
    const activeApiRef = useRef(activeApi);
    activeApiRef.current = activeApi;
    const [resultSet, setResultSet] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [total, setTotal] = useState(0);
    const requestIdRef = useRef(0);
    /** `serializedQuery` of the last successful Cube response; drives sync alignment (see below). */
    const lastSuccessSerializedRef = useRef(null);
    const { pageIndex, pageSize } = options.pagination;
    // Serialize query to a stable string to prevent infinite re-renders
    // when the query object reference changes but the content is the same
    const serializedQuery = useMemo(() => (query ? JSON.stringify(query) : ''), [query]);
    // Synchronous alignment: any `setTotal(0)` inside `useEffect` runs *after* paint. Filter dropdowns
    // read `total` on that paint to compute `hasMore`; stale totals + empty options make Nucleus Select
    // auto "fill viewport" call `onLoadMore` in a tight loop. Only expose totals/rows once they match
    // the currently active query string (updated on each successful fetch).
    const alignedWithActiveQuery = !query || options.skip ? true : lastSuccessSerializedRef.current === serializedQuery;
    const effectiveTotal = alignedWithActiveQuery ? total : 0;
    const effectiveResultSet = alignedWithActiveQuery ? resultSet : null;
    useEffect(() => {
        if (!query || !cubeApi || options.skip) {
            return;
        }
        const currentRequestId = ++requestIdRef.current;
        setIsLoading(true);
        setError(null);
        options.onQueryExecuted?.();
        const controller = new AbortController();
        const { signal } = controller;
        const fetchPage = async () => {
            try {
                const paginatedQuery = {
                    ...query,
                    total: true,
                    offset: pageIndex * pageSize,
                    limit: pageSize,
                };
                const result = await activeApiRef.current.load(paginatedQuery, { cache: 'must-revalidate', signal });
                if (currentRequestId !== requestIdRef.current) {
                    return;
                }
                const loadResponse = result.loadResponse?.results?.[0];
                const totalCount = loadResponse?.total ?? 0;
                lastSuccessSerializedRef.current = serializedQuery;
                setResultSet(result);
                setTotal(totalCount);
                setIsLoading(false);
            }
            catch (err) {
                if (err instanceof Error && err.name === 'AbortError') {
                    return;
                }
                if (currentRequestId !== requestIdRef.current) {
                    return;
                }
                setError(err instanceof Error ? err : new Error('Failed to fetch data'));
                setIsLoading(false);
            }
        };
        fetchPage();
        return () => {
            controller.abort();
        };
    }, [serializedQuery, cubeApi, options.skip, pageIndex, pageSize, options.refreshNonce]);
    return {
        resultSet: effectiveResultSet,
        // `alignedWithActiveQuery` flips to false synchronously during render (the moment `query`
        // changes), but the `isLoading` state only flips true inside the effect above, which runs a
        // tick later. That gap produced one render with `isLoading: false` + `resultSet: null` —
        // ChartContent reads that as "loaded, no data" and unmounts TableChart, wiping its sort state
        // (SSP sort-fix investigation: this was the actual cause of the sort highlight not sticking).
        isLoading: isLoading || !alignedWithActiveQuery,
        error,
        total: effectiveTotal,
    };
};
